import { useState } from 'react';
import { Search, Reply, EyeOff, Sparkles, Send, MessageCircle } from 'lucide-react';
import { useTiktokComments, useTiktokCommentReply, useTiktokCommentHide } from '@/api/useTiktok';
import type { SocialComment } from '@/types/outreach';
import toast from 'react-hot-toast';
import styles from './TiktokCommentFeed.module.css';

function formatRelativeTime(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function SentimentBadge({ score }: { score: number | null }) {
  if (score === null) return null;
  let label = 'Neutral';
  let cls = styles.sentimentNeutral;
  if (score >= 0.5) {
    label = 'Positive';
    cls = styles.sentimentPositive;
  } else if (score <= -0.5) {
    label = 'Negative';
    cls = styles.sentimentNegative;
  }
  return <span className={`${styles.sentiment} ${cls}`}>{label}</span>;
}

interface CommentCardProps {
  comment: SocialComment;
}

function CommentCard({ comment }: CommentCardProps) {
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [useAi, setUseAi] = useState(false);
  const reply = useTiktokCommentReply();
  const hide = useTiktokCommentHide();

  const handleSendReply = () => {
    if (!replyText.trim()) return;
    reply.mutate(
      { commentId: comment.id, content: replyText },
      {
        onSuccess: () => {
          toast.success('Reply sent');
          setReplyText('');
          setShowReply(false);
        },
        onError: () => toast.error('Failed to send reply'),
      },
    );
  };

  const handleHide = () => {
    hide.mutate(
      { commentId: comment.id, hidden: true },
      {
        onSuccess: () => toast.success('Comment hidden'),
        onError: () => toast.error('Failed to hide comment'),
      },
    );
  };

  return (
    <div className={`${styles.card} ${comment.isReply ? styles.cardIndented : ''}`}>
      <div className={styles.cardHeader}>
        <div className={styles.authorRow}>
          {comment.authorProfilePic ? (
            <img
              src={comment.authorProfilePic}
              alt={comment.authorName}
              className={styles.avatar}
            />
          ) : (
            <div className={styles.avatarPlaceholder}>
              {comment.authorName.charAt(0).toUpperCase()}
            </div>
          )}
          <div className={styles.authorMeta}>
            <span className={styles.authorName}>@{comment.authorName}</span>
            <span className={styles.time}>{formatRelativeTime(comment.timestamp)}</span>
          </div>
        </div>
        <div className={styles.badges}>
          <SentimentBadge score={comment.sentimentScore} />
          {comment.autoReplied && <span className={styles.autoRepliedBadge}>Auto-replied</span>}
        </div>
      </div>

      <p className={styles.message}>{comment.message}</p>

      {comment.autoReplied && comment.autoReplyContent && (
        <div className={styles.autoReplyBox}>
          <span className={styles.autoReplyLabel}>AI reply:</span>
          <p className={styles.autoReplyContent}>{comment.autoReplyContent}</p>
        </div>
      )}

      <div className={styles.actions}>
        <button
          className={`${styles.actionBtn} ${showReply ? styles.actionBtnActive : ''}`}
          onClick={() => setShowReply((v) => !v)}
        >
          <Reply className={styles.actionIcon} />
          Reply
        </button>
        <button className={styles.actionBtn} onClick={handleHide} disabled={hide.isPending}>
          <EyeOff className={styles.actionIcon} />
          Hide
        </button>
      </div>

      {showReply && (
        <div className={styles.replyForm}>
          <div className={styles.replyToggle}>
            <button
              className={`${styles.toggleBtn} ${!useAi ? styles.toggleBtnActive : ''}`}
              onClick={() => setUseAi(false)}
            >
              Manual
            </button>
            <button
              className={`${styles.toggleBtn} ${useAi ? styles.toggleBtnActive : ''}`}
              onClick={() => setUseAi(true)}
            >
              <Sparkles className={styles.sparkIcon} /> AI
            </button>
          </div>
          {useAi ? (
            <p className={styles.aiNote}>AI will auto-generate reply based on comment context.</p>
          ) : (
            <div className={styles.replyInputRow}>
              <input
                type="text"
                placeholder="Write a reply..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                className={styles.replyInput}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendReply();
                  }
                }}
              />
              <button
                className={styles.sendBtn}
                onClick={handleSendReply}
                disabled={reply.isPending || !replyText.trim()}
              >
                <Send className={styles.sendIcon} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface TiktokCommentFeedProps {
  videoId: string | null;
}

export function TiktokCommentFeed({ videoId }: TiktokCommentFeedProps) {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useTiktokComments(videoId, search || undefined);
  const comments = data?.results ?? [];

  if (!videoId) {
    return (
      <div className={styles.empty}>
        <MessageCircle className={styles.emptyIcon} />
        <p>Select a video to view its comments</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.searchWrapper}>
          <Search className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search comments..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        {data && <span className={styles.count}>{data.count} comments</span>}
      </div>

      <div className={styles.feed}>
        {isLoading && <div className={styles.emptyInner}>Loading comments...</div>}
        {!isLoading && comments.length === 0 && (
          <div className={styles.emptyInner}>No comments found for this video.</div>
        )}
        {comments.map((c) => (
          <CommentCard key={c.id} comment={c} />
        ))}
      </div>
    </div>
  );
}
